import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  CircleNotch,
  ArrowRight,
  ShieldCheck,
  Info,
  Warning,
  UserCircle,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, getProviderName, getPrimarySpecialty } from "../lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = API_URL || "";
const NAME_PAGE_SIZE = 10;

const PecosLookup = () => {
  const { npiParam, lastName: lastParam, firstName: firstParam } = useParams();
  const navigate = useNavigate();

  // Determine mode from URL
  const urlMode = npiParam ? "npi" : (lastParam ? "name" : null);

  const [mode, setMode] = useState(urlMode || "npi");
  const [npiInput, setNpiInput] = useState(npiParam || "");
  const [firstName, setFirstName] = useState(firstParam?.replace(/-/g, " ") || "");
  const [lastName, setLastName] = useState(lastParam?.replace(/-/g, " ") || "");

  const [loading, setLoading] = useState(false);
  const [pecosResult, setPecosResult] = useState(null);
  const [nameResults, setNameResults] = useState(null);
  const [nameHasMore, setNameHasMore] = useState(false);
  const [namePage, setNamePage] = useState(1);
  const [selectedNpi, setSelectedNpi] = useState(null);
  const [error, setError] = useState(null);

  // Auto-load from URL params
  useEffect(() => {
    if (npiParam && npiParam.length === 10) {
      lookupByNpi(npiParam);
    } else if (lastParam) {
      const fn = firstParam?.replace(/-/g, " ") || "";
      const ln = lastParam.replace(/-/g, " ");
      setFirstName(fn);
      setLastName(ln);
      setMode("name");
      searchByNameDirect(fn, ln, 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiParam, lastParam, firstParam]);

  const lookupByNpi = async (npi) => {
    const n = npi || npiInput;
    if (!n || n.length !== 10) {
      setError("Please enter a valid 10-digit NPI number.");
      return;
    }
    setError(null);
    setLoading(true);
    setPecosResult(null);
    setNameResults(null);
    setSelectedNpi(null);

    try {
      const [pecosRes, providerRes] = await Promise.all([
        fetch(`${API_URL}/api/pecos/check/${n}`).then(r => r.json()),
        api.searchProviders({ number: n, limit: 1 }).catch(() => null),
      ]);
      const provider = providerRes?.results?.[0] || null;
      setPecosResult({ npi: n, enrolled: pecosRes.enrolled, status: pecosRes.status, data: pecosRes.data, provider });
    } catch {
      setPecosResult({ npi: n, enrolled: null, status: "unavailable", data: null, provider: null });
    } finally {
      setLoading(false);
    }
  };

  const searchByNameDirect = async (fn, ln, page) => {
    setLoading(true);
    setPecosResult(null);
    setNameResults(null);
    setSelectedNpi(null);
    setNamePage(page);

    try {
      const params = { last_name: ln, limit: NAME_PAGE_SIZE, skip: (page - 1) * NAME_PAGE_SIZE };
      if (fn) params.first_name = fn;
      const data = await api.searchProviders(params);
      setNameResults(data.results || []);
      setNameHasMore(data.has_more || false);
    } catch {
      setNameResults([]);
      setNameHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNpiSearch = () => {
    if (!npiInput || npiInput.length !== 10) {
      setError("Please enter a valid 10-digit NPI number.");
      return;
    }
    navigate(`/pecos-lookup/npi/${npiInput}`);
  };

  const handleNameSearch = () => {
    if (!lastName.trim()) {
      setError("Please enter at least a last name.");
      return;
    }
    setError(null);
    const ln = lastName.trim().toLowerCase().replace(/\s+/g, "-");
    const fn = firstName.trim().toLowerCase().replace(/\s+/g, "-");
    if (fn) {
      navigate(`/pecos-lookup/name/${ln}/${fn}`);
    } else {
      navigate(`/pecos-lookup/name/${ln}`);
    }
  };

  const handleNamePageChange = (newPage) => {
    const ln = lastName.trim();
    const fn = firstName.trim();
    searchByNameDirect(fn, ln, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const checkSelectedProvider = async (npi) => {
    setSelectedNpi(npi);
    setLoading(true);
    setPecosResult(null);

    try {
      const pecosRes = await fetch(`${API_URL}/api/pecos/check/${npi}`).then(r => r.json());
      const provider = nameResults?.find(r => r.number?.toString() === npi) || null;
      setPecosResult({ npi, enrolled: pecosRes.enrolled, status: pecosRes.status, data: pecosRes.data, provider });
    } catch {
      setPecosResult({ npi, enrolled: null, status: "unavailable", data: null, provider: null });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (mode === "npi") handleNpiSearch();
    else handleNameSearch();
  };

  // SEO
  const isLanding = !npiParam && !lastParam;
  const buildTitle = () => {
    if (npiParam && pecosResult?.provider) {
      const name = getProviderName(pecosResult.provider);
      return `PECOS NPI Lookup — ${name} (NPI ${npiParam})`;
    }
    if (npiParam) return `PECOS NPI Lookup — NPI Number ${npiParam} Medicare Status`;
    if (lastParam) {
      const name = firstParam ? `${firstParam.replace(/-/g, " ")} ${lastParam.replace(/-/g, " ")}` : lastParam.replace(/-/g, " ");
      return `PECOS Lookup for ${name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")} | NPI Lookup`;
    }
    return "PECOS NPI Lookup — Free Medicare Enrollment Check | NPI Lookup";
  };

  const buildDescription = () => {
    if (npiParam && pecosResult) {
      const enrolled = pecosResult.enrolled ? "is enrolled" : "is not found";
      return `NPI number ${npiParam} ${enrolled} in Medicare's PECOS system. Free PECOS NPI lookup — verify Medicare enrollment for any provider by NPI number or name.`;
    }
    if (lastParam) {
      const name = firstParam ? `${firstParam.replace(/-/g, " ")} ${lastParam.replace(/-/g, " ")}` : lastParam.replace(/-/g, " ");
      return `PECOS NPI lookup for ${name}. Find providers by name, verify Medicare enrollment status, and cross-reference the NPI number in the NPPES registry.`;
    }
    return "Free PECOS NPI lookup. Check if a provider is enrolled in Medicare's PECOS by NPI number or name — cross-referenced with the NPPES national NPI registry.";
  };

  const canonicalUrl = () => {
    if (npiParam) return `${SITE_URL}/pecos-lookup/npi/${npiParam}`;
    if (lastParam && firstParam) return `${SITE_URL}/pecos-lookup/name/${lastParam}/${firstParam}`;
    if (lastParam) return `${SITE_URL}/pecos-lookup/name/${lastParam}`;
    return `${SITE_URL}/pecos-lookup`;
  };

  return (
    <>
      <Helmet>
        <title>{buildTitle()}</title>
        <meta name="description" content={buildDescription()} />
        <link rel="canonical" href={canonicalUrl()} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "PECOS Medicare Enrollment Lookup",
          "description": "Free tool to check provider enrollment in CMS PECOS system",
          "url": `${SITE_URL}/pecos-lookup`,
          "applicationCategory": "HealthApplication",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          "name": "PECOS Medicare Enrollment Data",
          "description": "Provider Enrollment, Chain, and Ownership System data from CMS. Verify Medicare enrollment status for healthcare providers.",
          "url": `${SITE_URL}/pecos-lookup`,
          "license": "https://www.usa.gov/government-works",
          "creator": { "@type": "Organization", "name": "Centers for Medicare & Medicaid Services (CMS)" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "PECOS Lookup" }
          ]
        })}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3">
                <ShieldCheck className="w-4 h-4 text-teal-300" weight="fill" />
                <span className="text-teal-200 text-sm font-medium">Medicare Enrollment Check</span>
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-2" data-testid="pecos-title">
                PECOS Lookup
              </h1>
              <p className="text-slate-300 text-base max-w-xl mx-auto">
                Check if a healthcare provider is enrolled in Medicare's PECOS system.
                Search by NPI number or provider name.
              </p>
            </div>

            {/* Mode Tabs + Search Form */}
            <div className="max-w-xl mx-auto">
              <div className="bg-white rounded-2xl p-2 shadow-2xl shadow-black/20">
                {/* Mode Toggle */}
                <div className="flex gap-1 mb-2 bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={() => { setMode("npi"); setError(null); }}
                    className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
                      mode === "npi" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="pecos-tab-npi"
                  >
                    Search by NPI
                  </button>
                  <button
                    onClick={() => { setMode("name"); setError(null); }}
                    className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
                      mode === "name" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="pecos-tab-name"
                  >
                    Search by Name
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {mode === "npi" ? (
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter 10-digit NPI number"
                          value={npiInput}
                          onChange={(e) => setNpiInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className="h-12 pl-11 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl font-mono"
                          data-testid="pecos-npi-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading || npiInput.length !== 10}
                        className="h-12 px-6 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl shrink-0"
                        data-testid="pecos-search-btn"
                      >
                        {loading && !selectedNpi ? <CircleNotch className="w-5 h-5 animate-spin" /> : "Check"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="First name (optional)"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="pecos-first-name"
                        />
                        <Input
                          type="text"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="pecos-last-name"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading && !selectedNpi || !lastName.trim()}
                        className="w-full h-12 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl"
                        data-testid="pecos-name-search-btn"
                      >
                        {loading && !selectedNpi ? <CircleNotch className="w-5 h-5 animate-spin mr-2" /> : null}
                        Find Providers
                      </Button>
                    </div>
                  )}
                </form>
              </div>
              {error && <p className="text-red-400 text-sm text-center mt-2" data-testid="pecos-error">{error}</p>}
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        {!isLanding && (
          <nav className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-sm" data-testid="pecos-breadcrumbs">
            <ol className="flex items-center gap-2 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to="/pecos-lookup" className="hover:text-teal">PECOS Lookup</Link></li>
              {npiParam && (
                <>
                  <li>/</li>
                  <li className="text-slate-900 font-medium">NPI {npiParam}</li>
                </>
              )}
              {lastParam && (
                <>
                  <li>/</li>
                  <li className="text-slate-900 font-medium capitalize">{lastParam.replace(/-/g, " ")}{firstParam ? `, ${firstParam.replace(/-/g, " ")}` : ""}</li>
                </>
              )}
            </ol>
          </nav>
        )}

        {/* Results */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* PECOS Result Card */}
          {pecosResult && (
            <div className="mb-6" data-testid="pecos-result-card">
              <ResultCard result={pecosResult} />
            </div>
          )}

          {/* Name Search Results */}
          {nameResults && nameResults.length > 0 && (
            <div data-testid="pecos-name-results">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                Providers Found
              </h2>
              <p className="text-sm text-slate-500 mb-4">Select a provider to check their PECOS enrollment status.</p>
              <div className="space-y-2">
                {nameResults.map((provider) => {
                  const pNpi = provider.number?.toString();
                  const isSelected = selectedNpi === pNpi;
                  const isChecking = loading && selectedNpi === pNpi;
                  return (
                    <button
                      key={pNpi}
                      onClick={() => checkSelectedProvider(pNpi)}
                      disabled={isChecking}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isSelected
                          ? "border-teal bg-teal/5 ring-1 ring-teal/20"
                          : "border-slate-200 bg-white hover:border-teal/40 hover:shadow-sm"
                      }`}
                      data-testid={`name-result-${pNpi}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{getProviderName(provider)}</p>
                          <p className="text-sm text-slate-500">
                            NPI: <span className="font-mono">{pNpi}</span>
                            {getPrimarySpecialty(provider) && <> &middot; {getPrimarySpecialty(provider)}</>}
                          </p>
                        </div>
                        <div className="shrink-0 ml-3">
                          {isChecking ? (
                            <CircleNotch className="w-5 h-5 text-teal animate-spin" />
                          ) : (
                            <ArrowRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Pagination */}
              {(nameHasMore || namePage > 1) && (
                <div className="flex items-center justify-center gap-3 mt-6" data-testid="pecos-name-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNamePageChange(namePage - 1)}
                    disabled={namePage === 1}
                  >
                    <CaretLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <span className="text-sm text-slate-500 font-medium px-3">Page {namePage}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNamePageChange(namePage + 1)}
                    disabled={!nameHasMore}
                  >
                    Next <CaretRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* No Name Results */}
          {nameResults && nameResults.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="pecos-no-results">
              <UserCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">No providers found</h3>
              <p className="text-slate-500 text-sm">
                No NPI-registered providers match that name. Try adjusting the spelling or searching by NPI number.
              </p>
            </div>
          )}
        </section>

        {/* Explainer Section */}
        <section className="bg-slate-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pecos-explainer">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-navy/10 flex items-center justify-center mb-3">
                  <Info className="w-5 h-5 text-navy" weight="fill" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">What is PECOS?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  PECOS stands for Provider Enrollment, Chain, and Ownership System. It is Medicare's online enrollment
                  database managed by CMS (Centers for Medicare & Medicaid Services).
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center mb-3">
                  <Warning className="w-5 h-5 text-teal" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">Why does it matter?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Medicare claims are rejected if an ordering or referring provider is not found in PECOS. Billing teams
                  and DME suppliers must verify PECOS status before submitting claims.
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" weight="fill" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">How current is the data?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  The CMS Order & Referring dataset is updated monthly. Results reflect the latest available CMS data
                  for provider enrollment verification.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display font-semibold text-xl text-slate-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3" data-testid="pecos-faq">
            {[
              { q: "What does PECOS enrollment mean?", a: "A provider enrolled in PECOS is authorized to order and refer services for Medicare beneficiaries. Without PECOS enrollment, claims referencing that provider as the ordering/referring physician will be denied." },
              { q: "How is PECOS different from NPI registration?", a: "NPI is a universal provider identifier required for all HIPAA transactions. PECOS is specifically for Medicare enrollment. A provider can have an NPI but not be PECOS-enrolled (they wouldn't be able to order/refer Medicare services)." },
              { q: "What do Part B, DME, HHA, and PMD mean?", a: "These are Medicare enrollment categories. Part B = physician services, DME = Durable Medical Equipment, HHA = Home Health Agency, PMD = Power Mobility Devices. A 'Y' means the provider is eligible to order/refer in that category." },
              { q: "How often is the data updated?", a: "CMS updates the Order & Referring dataset monthly. Our tool queries this dataset in real-time, so results always reflect the latest published CMS data." },
            ].map((faq, i) => (
              <details key={i} className="group bg-white border border-slate-200 rounded-lg">
                <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50/50 transition-colors rounded-lg">
                  <span className="font-medium text-slate-900 text-sm pr-4">{faq.q}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                </summary>
                <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

// ---- Result Card Component ----
const ResultCard = ({ result }) => {
  const { npi, enrolled, status, data, provider } = result;
  const providerName = provider ? getProviderName(provider) : (data ? `${data.first_name} ${data.last_name}` : `NPI ${npi}`);
  const specialty = provider ? getPrimarySpecialty(provider) : null;

  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
      <div className={`px-5 py-4 ${
        enrolled === true ? "bg-emerald-50 border-b-2 border-emerald-200" :
        enrolled === false ? "bg-red-50 border-b-2 border-red-200" :
        "bg-slate-50 border-b-2 border-slate-200"
      }`}>
        <div className="flex items-center gap-3">
          {enrolled === true ? (
            <CheckCircle className="w-8 h-8 text-emerald-600 shrink-0" weight="fill" />
          ) : enrolled === false ? (
            <XCircle className="w-8 h-8 text-red-500 shrink-0" weight="fill" />
          ) : (
            <Warning className="w-8 h-8 text-slate-400 shrink-0" />
          )}
          <div>
            <p className={`font-display font-bold text-lg ${
              enrolled === true ? "text-emerald-700" :
              enrolled === false ? "text-red-600" :
              "text-slate-500"
            }`} data-testid="pecos-status-text">
              {enrolled === true ? "PECOS Enrolled" :
               enrolled === false ? "Not Found in PECOS" :
               "PECOS Status Unavailable"}
            </p>
            {enrolled === true && (
              <p className="text-sm text-emerald-600 font-medium">
                Eligible to Order & Refer in Medicare: <span className="font-bold">Yes</span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-3">
        <div>
          <p className="font-semibold text-slate-900 text-lg" data-testid="pecos-provider-name">{providerName}</p>
          <p className="text-sm text-slate-500">
            NPI: <span className="font-mono font-medium">{npi}</span>
            {specialty && <> &middot; {specialty}</>}
          </p>
        </div>

        {enrolled && data && (
          <div>
            <p className="text-xs text-slate-500 font-medium mb-2 uppercase tracking-wide">Enrollment Categories</p>
            <div className="flex flex-wrap gap-2">
              <EnrollmentBadge label="Part B" enrolled={data.partb} />
              <EnrollmentBadge label="DME" enrolled={data.dme} />
              <EnrollmentBadge label="HHA" enrolled={data.hha} />
              <EnrollmentBadge label="PMD" enrolled={data.pmd} />
              <EnrollmentBadge label="Hospice" enrolled={data.hospice} />
            </div>
          </div>
        )}

        {enrolled === false && (
          <p className="text-sm text-slate-500">
            This provider was not found in the CMS Order & Referring dataset. They may not be currently enrolled to order or refer Medicare services.
          </p>
        )}

        {enrolled === null && (
          <p className="text-sm text-slate-400">
            Unable to verify PECOS status at this time. The CMS API may be temporarily unavailable.
          </p>
        )}

        <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-4">
          <Link
            to={`/provider/${npi}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
            data-testid="pecos-view-profile-link"
          >
            View Full NPI Profile <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={`/verify?npi=${npi}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-teal transition-colors"
          >
            Verification Report <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={`/dea-npi-lookup?npi=${npi}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-teal transition-colors"
          >
            DEA Lookup <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="text-xs text-slate-400">Data sourced from CMS Order & Referring dataset. Updated monthly.</p>
      </div>
    </div>
  );
};

const EnrollmentBadge = ({ label, enrolled }) => (
  <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 border ${
    enrolled
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-slate-50 text-slate-400 border-slate-200"
  }`}>
    {enrolled ? <CheckCircle className="w-3.5 h-3.5" weight="fill" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

export default PecosLookup;
